import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, ChevronDown, ChevronUp, BookOpen, Gift, Users, Truck, Leaf, Award } from "lucide-react";
import { useCart } from "@/contexts/CartContext";

const TESTIMONIALS = [
  { name: "Sarah M.", role: "Parent of 3", text: "This book changed how my kids approach challenges. The Success Pipeline concept is something we talk about every day. Absolutely worth every penny!", stars: 5 },
  { name: "David R.", role: "Elementary Teacher", text: "I ordered 25 copies for my classroom. The farming story makes success principles accessible to every child, even those who struggle with reading.", stars: 5 },
  { name: "Jennifer L.", role: "Parent & Community Leader", text: "We bought the 2-book option — one for our son, one to gift forward. The teacher called us to say it was the best gift the class had ever received.", stars: 5 },
  { name: "Marcus T.", role: "Parent", text: "My daughter finished it in one sitting! She immediately started applying the 6 success principles to her goals. Incredible book for young minds.", stars: 5 },
];

const FAQS = [
  { q: "What age range is this book for?", a: "Expect to Win Kids Edition is designed for children ages 6-14, but the principles are timeless for all ages. It's a perfect read-aloud for younger children and an independent read for older ones." },
  { q: "What is the Gift it Forward movement?", a: "Our mission is to put success-building books into the hands of every child. When you purchase a bundle, a portion helps bring books to communities that need them most. Every purchase helps a child WIN." },
  { q: "How long does shipping take?", a: "Standard shipping takes 5-10 business days within the US. You'll receive a tracking number as soon as your order ships." },
  { q: "What is the digital book version?", a: "The digital version ($5.95) gives you instant access to an interactive reader right in your browser — perfect for iPad and all devices. No waiting, read immediately after purchase." },
  { q: "What do I get with the Gift Starter (10 books)?", a: "You receive 10 physical hardcopy books with FREE shipping, plus our promise that the NEXT book we publish is yours FREE. It's the best way to share this with your community." },
  { q: "Can I gift the digital book?", a: "Absolutely! Create an account for the recipient, then they can access the book in their library. Or purchase the 2-book option to gift a physical copy to someone you care about." },
];

export default function Home() {
  const { addToCart } = useCart();
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="overflow-hidden" style={{ backgroundColor: "#FDFBF7" }}>
      {/* Urgent Banner */}
      <div className="bg-[#2F5233] text-white text-center py-2 px-4 text-sm font-semibold" data-testid="promo-banner">
        Flash Membership Drive — Limited Quantities Available at Current Prices
      </div>

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 overflow-hidden">
          <img src="https://images.pexels.com/photos/4968181/pexels-photo-4968181.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750" alt="Child reading in nature" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#FDFBF7] via-[#FDFBF7]/90 to-[#B2D3C2]/30" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center py-20">
          <div className="space-y-6 fade-in-up">
            <div className="inline-flex items-center gap-2 bg-[#2F5233]/10 text-[#2F5233] px-4 py-2 rounded-full text-sm font-semibold">
              <Leaf className="w-4 h-4" /> The Gift it Forward Movement
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-[#1A1A1A] leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Give Your Child the Tools to{" "}
              <span className="text-[#2F5233] italic">WIN</span> in Life
            </h1>
            <p className="text-lg text-[#5A5A5A] leading-relaxed max-w-xl">
              <em>Expect to Win Kids Edition</em> by Kyle Keith teaches children the proven 6-step Success Pipeline — the same principles behind every great achievement.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Link to="/shop" className="bg-[#2F5233] text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-[#1E3A21] transition-all hover:scale-105 flex items-center gap-2 shadow-lg" data-testid="hero-shop-cta">
                Get Your Books <ArrowRight className="w-5 h-5" />
              </Link>
              <a href="#about" className="border-2 border-[#2F5233] text-[#2F5233] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#2F5233]/5 transition-all">
                Learn More
              </a>
            </div>
            <div className="flex items-center gap-6 pt-2">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#2F5233]">10K+</p>
                <p className="text-xs text-[#5A5A5A]">Books Delivered</p>
              </div>
              <div className="w-px h-10 bg-[#E6E2D3]" />
              <div className="text-center">
                <p className="text-2xl font-bold text-[#2F5233]">500+</p>
                <p className="text-xs text-[#5A5A5A]">Classrooms Reached</p>
              </div>
              <div className="w-px h-10 bg-[#E6E2D3]" />
              <div className="text-center">
                <p className="text-2xl font-bold text-[#2F5233]">4.9★</p>
                <p className="text-xs text-[#5A5A5A]">Parent Rating</p>
              </div>
            </div>
          </div>

          {/* Book visual */}
          <div className="flex justify-center fade-in-up-2">
            <div className="relative animate-float">
              <div className="book-cover-3d rounded-2xl shadow-2xl overflow-hidden border-4 border-white/60" style={{ maxWidth: "340px" }}>
                <img
                  src="/images/book-cover.jpg"
                  alt="Expect to Win Kids Edition by Kyle Keith"
                  className="w-full h-auto block"
                />
              </div>
              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 bg-[#D2691E] text-white text-xs font-bold px-3 py-2 rounded-full shadow-lg">
                From $5.95
              </div>
              <div className="absolute -bottom-4 -left-4 bg-[#F4D03F] text-[#1A1A1A] text-xs font-bold px-3 py-2 rounded-full shadow-lg">
                iPad Friendly
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Book */}
      <section id="about" className="py-20 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-[#B2D3C2] text-[#2F5233] px-4 py-2 rounded-full text-sm font-semibold">
                <BookOpen className="w-4 h-4" /> About the Book
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                The Success Pipeline Every Child Needs
              </h2>
              <p className="text-[#5A5A5A] leading-relaxed text-lg">
                Through the engaging story of two farmers, Kyle and Chris, children discover that success isn't luck — it's a <strong>pipeline</strong>. Miss one step, and nothing flows. Fill every step, and you WIN.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: "1", label: "Believe in Yourself" },
                  { icon: "2", label: "Imagine What Can Be" },
                  { icon: "3", label: "Set Up a Plan" },
                  { icon: "4", label: "Have Strong Desire" },
                  { icon: "5", label: "Work with Discipline" },
                  { icon: "6", label: "Live with Honesty" },
                ].map(item => (
                  <div key={item.icon} className="flex items-center gap-3 p-3 bg-[#FDFBF7] rounded-xl border border-[#E6E2D3]">
                    <div className="w-8 h-8 bg-[#2F5233] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {item.icon}
                    </div>
                    <span className="text-sm font-semibold text-[#1A1A1A]">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-6">
              <img src="https://images.pexels.com/photos/6692846/pexels-photo-6692846.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Parent reading to child" className="rounded-3xl shadow-xl w-full object-cover h-80" />
              <div className="flex items-center gap-3 bg-[#2F5233]/5 p-4 rounded-2xl border border-[#2F5233]/20">
                <Award className="w-8 h-8 text-[#D2691E] flex-shrink-0" />
                <div>
                  <p className="font-bold text-[#1A1A1A]">Endorsed by Monte Holm</p>
                  <p className="text-sm text-[#5A5A5A]">Best-selling author & success coach</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gift it Forward Movement */}
      <section className="py-20" style={{ backgroundColor: "#FDFBF7" }} data-testid="movement-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#D2691E]/10 text-[#D2691E] px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Gift className="w-4 h-4" /> The Movement
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            What if paying it forward once<br />gave you access to our library{" "}
            <span className="text-[#2F5233] italic">FOREVER?</span>
          </h2>
          <p className="text-lg text-[#5A5A5A] max-w-3xl mx-auto leading-relaxed mb-12">
            Our Gift it Forward membership is a one-time investment that keeps giving. You receive books, you share books, and together we build a generation of winners.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: <BookOpen className="w-8 h-8" />, title: "Receive Your Books", desc: "Physical hardcopy books shipped directly to your door. High quality, beautifully illustrated." },
              { icon: <Gift className="w-8 h-8" />, title: "Gift it Forward", desc: "Share a book with a family who needs it. Every copy you share plants a seed of success." },
              { icon: <Users className="w-8 h-8" />, title: "Join the Community", desc: "Connect with parents and educators who believe in raising the next generation of winners." },
            ].map((item, i) => (
              <div key={i} className="bg-white p-8 rounded-3xl border border-[#E6E2D3] hover:border-[#2F5233]/30 transition-all hover:-translate-y-1">
                <div className="w-16 h-16 bg-[#B2D3C2] rounded-2xl flex items-center justify-center text-[#2F5233] mx-auto mb-4">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                <p className="text-[#5A5A5A] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Shop Preview */}
      <section className="py-20 bg-[#2F5233]" data-testid="quick-shop-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-[#FDFBF7] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Choose Your Impact Level
          </h2>
          <p className="text-[#B2D3C2] text-lg mb-12">Every option includes free shipping (except single book). No subscriptions, ever.</p>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { label: "Gift Starter", price: "$85", books: "10 Books", perBook: "$8.50/book", desc: "Perfect for sharing with your community" },
              { label: "Gift Champion", price: "$170", books: "25 Books", perBook: "$6.80/book", desc: "For schools, churches & group leaders", highlight: true },
              { label: "Lifetime Member", price: "$425", books: "50 Books", perBook: "$8.50/book", desc: "Unlimited impact — books for life" },
            ].map((tier, i) => (
              <div key={i} className={`p-6 rounded-3xl text-left ${tier.highlight ? 'bg-[#F4D03F] text-[#1A1A1A]' : 'bg-white/10 text-white border border-white/20'}`}>
                {tier.highlight && <div className="text-xs font-bold text-[#D2691E] mb-2 uppercase tracking-wide">Most Popular</div>}
                <div className="text-3xl font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{tier.price}</div>
                <div className="font-bold text-lg mb-1">{tier.label}</div>
                <div className="text-sm mb-1">{tier.books}</div>
                <div className="text-xs opacity-70 mb-3">{tier.perBook}</div>
                <p className={`text-sm ${tier.highlight ? 'text-[#5A5A5A]' : 'text-white/70'}`}>{tier.desc}</p>
              </div>
            ))}
          </div>
          <Link to="/shop" className="inline-flex items-center gap-2 bg-[#F4D03F] text-[#1A1A1A] px-10 py-4 rounded-full font-bold text-lg hover:bg-[#e8c91e] transition-all hover:scale-105 shadow-lg" data-testid="view-all-products-btn">
            View All Options <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Playfair Display', serif" }}>
              What Parents Are Saying
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-[#FDFBF7] p-6 rounded-3xl border border-[#E6E2D3] hover:border-[#2F5233]/30 transition-all hover:-translate-y-1" data-testid={`testimonial-${i}`}>
                <div className="flex mb-3">
                  {Array(t.stars).fill(0).map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-[#F4D03F] fill-[#F4D03F]" />
                  ))}
                </div>
                <p className="text-[#5A5A5A] text-sm leading-relaxed mb-4 italic">"{t.text}"</p>
                <div>
                  <p className="font-bold text-sm text-[#1A1A1A]">{t.name}</p>
                  <p className="text-xs text-[#5A5A5A]">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20" style={{ backgroundColor: "#FDFBF7" }} data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className="bg-white border border-[#E6E2D3] rounded-2xl overflow-hidden" data-testid={`faq-${i}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-[#FDFBF7] transition-colors"
                >
                  <span className="font-semibold text-[#1A1A1A]">{faq.q}</span>
                  {openFaq === i ? <ChevronUp className="w-5 h-5 text-[#2F5233] flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-[#5A5A5A] flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6">
                    <p className="text-[#5A5A5A] leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-[#2F5233] p-12 rounded-3xl text-white">
            <Truck className="w-12 h-12 text-[#B2D3C2] mx-auto mb-4" />
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Ready to Join the Movement?
            </h2>
            <p className="text-[#B2D3C2] text-lg mb-8">Start with a single book for $16.95 or go all-in with a membership bundle. Every order helps a child WIN.</p>
            <Link to="/shop" className="inline-flex items-center gap-2 bg-[#F4D03F] text-[#1A1A1A] px-10 py-4 rounded-full font-bold text-xl hover:bg-[#e8c91e] transition-all hover:scale-105" data-testid="final-cta-btn">
              Shop Now — From $5.95 <ArrowRight className="w-6 h-6" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-[#2F5233] rounded-full flex items-center justify-center"><Leaf className="w-4 h-4 text-white" /></div>
                <span className="font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Children The Organic Way</span>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">Empowering children with the tools to WIN in life through organic, values-based education.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <div className="space-y-2">
                <Link to="/shop" className="block text-white/60 hover:text-white text-sm transition-colors">Shop Books</Link>
                <Link to="/my-library" className="block text-white/60 hover:text-white text-sm transition-colors">My Digital Library</Link>
                <Link to="/login" className="block text-white/60 hover:text-white text-sm transition-colors">Sign In</Link>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4">Our Promise</h4>
              <div className="space-y-2">
                {["100% Satisfaction Guarantee", "Free Shipping on Bundles", "Digital Access Immediately", "Future Books Guaranteed"].map(p => (
                  <div key={p} className="flex items-center gap-2 text-sm text-white/60">
                    <div className="w-1.5 h-1.5 bg-[#2F5233] rounded-full" />{p}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-white/40 text-xs">
            © 2024 Children The Organic Way. All rights reserved. | Expect to Win Kids Edition by Kyle Keith
          </div>
        </div>
      </footer>
    </div>
  );
}
